import type { CatalogOptionValueDto, ProductDetailDto, ProductVariantDto } from '@ecom/contracts';
import { deriveSku } from './fabrics';

/**
 * A product's variants as a matrix of its options (ADR-052).
 *
 * Variants are the combinations of the values a product comes in: seven
 * colours on five ways of selling is thirty-five. Adding a value to an option
 * adds a variant for every combination of the other options; adding a whole
 * option gives every variant the first value and copies them all for each
 * other. These functions plan those variants — SKUs, prices, what each holds —
 * for an operator to review before anything is written.
 */

/** A variant to create, as the operator reviews it. */
export interface PlannedVariant {
  /** Stable within one plan, for React keys and edits. */
  key: string;
  options: Record<string, string>;
  sku: string;
  /** Halalas; null follows the product's base price. */
  price: number | null;
  measureQuantity: number | null;
  isSample: boolean;
  maxPerOrder: number | null;
  weightGrams: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
}

/** An existing variant whose options change — the product gained an option. */
export interface PlannedUpdate {
  variant: ProductVariantDto;
  options: Record<string, string>;
}

/** The value a library entry brings: its code, and what a new variant with it starts from. */
export type LibraryValue = Pick<CatalogOptionValueDto, 'code' | 'measureQuantity' | 'isSample'>;

const without = (options: Record<string, string>, option: string) =>
  Object.fromEntries(Object.entries(options).filter(([name]) => name !== option));
const signature = (options: Record<string, string>) =>
  Object.entries(options)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => `${name}=${value}`)
    .join('&');

/** Each distinct combination of the other options the product's variants use, in variant order. */
function otherCombinations(product: ProductDetailDto, option: string): Record<string, string>[] {
  const seen = new Set<string>();
  const combos: Record<string, string>[] = [];
  for (const variant of product.variants) {
    const rest = without(variant.options, option);
    const key = signature(rest);
    if (!seen.has(key)) {
      seen.add(key);
      combos.push(rest);
    }
  }
  return combos.length > 0 ? combos : [{}];
}

/** The price a variant sets for itself, or null when it follows the base price. */
const ownPrice = (variant: ProductVariantDto, basePrice: number) => (variant.price === basePrice ? null : variant.price);

/**
 * New values for one option: a variant for each combination of the others.
 *
 * A **colour** (`copiesFacts`) is sold the ways the product already sells its
 * other colours, so each new variant copies the one it mirrors — same length,
 * same price, weight and limits — and its SKU follows that variant's pattern
 * (`…-BRASS-R10` → `…-EMERALD-R10`). A new **way of selling** (a roll, a
 * sample) is not a copy of anything: it takes what the library says it holds
 * and follows the base price, until someone prices it.
 */
export function planValues(
  product: ProductDetailDto,
  option: string,
  values: LibraryValue[],
  copiesFacts: boolean,
): PlannedVariant[] {
  const taken = new Set(product.variants.map((variant) => signature(variant.options)));
  const skus = new Set(product.variants.map((variant) => variant.sku.toUpperCase()));
  const plans: PlannedVariant[] = [];

  for (const value of values) {
    for (const rest of otherCombinations(product, option)) {
      const options = { ...rest, [option]: value.code };
      if (taken.has(signature(options))) continue;

      // The variant this one mirrors: same everything else, any value of this option.
      const mirror = product.variants.find((variant) => signature(without(variant.options, option)) === signature(rest));
      const fromCode = mirror?.options[option] ?? '';
      let sku = mirror ? deriveSku(mirror.sku, fromCode, value.code) : `${product.slug.toUpperCase()}-${value.code.toUpperCase()}`;
      for (let n = 2; skus.has(sku.toUpperCase()); n += 1) sku = `${sku.replace(/-\d+$/, '')}-${n}`;
      skus.add(sku.toUpperCase());

      const copy = copiesFacts && mirror;
      plans.push({
        key: signature(options),
        options,
        sku,
        price: copy ? ownPrice(mirror, product.basePrice) : value.isSample ? 0 : null,
        measureQuantity: copy ? mirror.measureQuantity : (value.measureQuantity ?? mirror?.measureQuantity ?? null),
        isSample: copy ? mirror.isSample : value.isSample,
        maxPerOrder: copy ? mirror.maxPerOrder : null,
        weightGrams: copy ? mirror.weightGrams : null,
        lengthMm: copy ? mirror.lengthMm : null,
        widthMm: copy ? mirror.widthMm : null,
        heightMm: copy ? mirror.heightMm : null,
      });
    }
  }
  return plans;
}

/**
 * A new option on a product that had none of it: every variant takes the
 * first value, and is copied once for each of the others. How the reference
 * dashboards add an option to a product that already has variants.
 */
export function planOption(
  product: ProductDetailDto,
  option: string,
  values: LibraryValue[],
): { updates: PlannedUpdate[]; creates: PlannedVariant[] } {
  const [first, ...rest] = values;
  if (!first) return { updates: [], creates: [] };

  const updates = product.variants.map((variant) => ({ variant, options: { ...variant.options, [option]: first.code } }));
  // Plan the rest against the product as it will be once every variant has the first value.
  const after: ProductDetailDto = {
    ...product,
    variants: product.variants.map((variant) => ({ ...variant, options: { ...variant.options, [option]: first.code } })),
  };
  return { updates, creates: planValues(after, option, rest, true) };
}

/** The variants that go with a value. */
export function variantsWith(product: ProductDetailDto, option: string, value: string): ProductVariantDto[] {
  return product.variants.filter((variant) => variant.options[option] === value);
}

/** The values an option uses on a product, in the library's order as the product's copies carry it. */
export function valuesOf(product: ProductDetailDto, option: string): string[] {
  const used = new Set(product.variants.map((variant) => variant.options[option]).filter(Boolean));
  const ordered = product.optionValues
    .filter((entry) => entry.option === option && used.has(entry.value))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((entry) => entry.value);
  return [...ordered, ...[...used].filter((value) => !ordered.includes(value))];
}
